// Names and params mirror mobile's AnalyticsEvents.kt. PRIVACY: never pass user content or PII (titles, answers, email); counts, enums, scores, booleans and ids only.
// Backend Ga4ParamPolicy drops unknown param keys: add new keys there too, or the metric reads zero silently.
import { logEvent } from './analytics'

export const AuthMethod = { PASSWORD: 'password', GOOGLE: 'google' } as const
export type AuthMethod = (typeof AuthMethod)[keyof typeof AuthMethod]

export function trackLogin(method: AuthMethod): void {
  void logEvent('login', { method })
}

/** GA4-reserved; mirrors mobile's `Analytics.signUp`, fired once a session exists. */
export function trackSignUp(method: AuthMethod): void {
  void logEvent('sign_up', { method })
}

/** [reason] must stay low-cardinality and non-PII; it is capped at 100 chars. */
export function trackLoginFailed(method: AuthMethod, reason: string): void {
  void logEvent('login_failed', { method, reason: reason.slice(0, 100) })
}

export function trackSessionStarted(questionCount: number): void {
  void logEvent('session_started', { question_count: String(questionCount) })
}

/** `expired` rows carry placeholder score and is_correct: exclude them from correctness rates. */
export function trackQuestionCompleted(
  questionIndex: number,
  score: number,
  isCorrect: boolean,
  expired = false,
): void {
  void logEvent('question_completed', {
    question_index: String(questionIndex),
    score: String(score),
    is_correct: String(isCorrect),
    expired: String(expired),
  })
}

export function trackSessionComplete(correctCount: number, totalCount: number, dailyGoalMet: boolean): void {
  void logEvent('session_complete', {
    correct_count: String(correctCount),
    total_count: String(totalCount),
    score_pct: String(totalCount > 0 ? Math.floor((correctCount * 100) / totalCount) : 0),
    daily_goal_met: String(dailyGoalMet),
  })
}

export const ImportSource = {
  CATALOG: 'catalog',
  MANUAL: 'manual',
  PDF: 'pdf',
  KOBO: 'kobo',
  KINDLE: 'kindle',
  READWISE: 'readwise',
} as const
export type ImportSource = (typeof ImportSource)[keyof typeof ImportSource]

export function trackImportStarted(source: ImportSource): void {
  void logEvent('import_started', { source })
}

export function trackImportCompleted(source: ImportSource, highlightCount: number, bookCount?: number): void {
  void logEvent('import_completed', {
    source,
    highlight_count: String(highlightCount),
    ...(bookCount != null ? { book_count: String(bookCount) } : {}),
  })
}

export function trackImportFailed(source: ImportSource, error?: string): void {
  void logEvent('import_failed', { source, error: (error ?? 'unknown').slice(0, 100) })
}

/** Web never sells, so this is the only paywall event. [trigger] must stay low-cardinality. */
export function trackPaywallViewed(trigger: string): void {
  void logEvent('paywall_viewed', { trigger })
}

/** `slug` is one of the known catalog slugs, not free text, so it passes the PII rule. */
export function trackTasteAnswered(slug: string): void {
  void logEvent('taste_answered', { slug })
}

export function trackTasteCapped(slug: string): void {
  void logEvent('taste_capped', { slug })
}

export function trackTasteSignupClicked(slug: string): void {
  void logEvent('taste_signup_clicked', { slug })
}
